import React from 'react';
import { Check, Plus, X } from '@phosphor-icons/react';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';
import type { JobMatchResult } from '../../utils/cv-check';
import { Button, Chip, Icon } from '../../design-system/ui';

const MAX_CHIPS = 8;

export interface CvMatchResultProps {
  result: JobMatchResult;
  /** Adds a skill the CV text already mentions to the skills list; only on the person's click. */
  onAddSkill?: (skill: string) => void;
  headingId: string;
}

/**
 * The CV-to-job comparison as chips and checks (TEC-711): matched, mentioned-but-not-listed,
 * missing, required languages and years. Shared by the job page card and the profile page's
 * "check against a job ad", so both read the same way.
 */
export function CvMatchResult({ result, onAddSkill, headingId }: CvMatchResultProps) {
  const formatMessage = useFormatMessage();
  const total = result.matched.length + result.mentioned.length + result.missing.length;
  const have = result.matched.length + result.mentioned.length;
  const addSkill = (skill: string) => onAddSkill?.(skill);

  return (
    <>
      {total > 0 ? (
        <>
          <div className="mt-1 flex items-baseline gap-2">
            <h2 id={headingId} className="type-subheading text-jc-ink">
              <Msg
                id="cvJobMatch.skillsHeadline"
                defaultMessage="Your CV shows {have} of the {total} skills this job lists"
                description="Headline of the job page CV match card; have/total are counts of the job's listed skills"
                values={{ have, total }}
              />
            </h2>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-jc-info bg-jc-elevated" aria-hidden>
            <div
              className="h-full rounded-jc-info bg-jc-selected"
              style={{ width: `${Math.round((have / total) * 100)}%` }}
            />
          </div>

          {result.matched.length > 0 ? (
            <ul
              className="mt-3 flex flex-wrap gap-1.5"
              aria-label={formatMessage({
                id: 'cvJobMatch.matchedLabel',
                defaultMessage: 'Skills on your CV',
                description: 'Accessible label for the list of matched skills',
              })}
            >
              {result.matched.slice(0, MAX_CHIPS).map((s) => (
                <li key={s}>
                  <Chip tone="positive">{s}</Chip>
                </li>
              ))}
            </ul>
          ) : null}

          {result.mentioned.length > 0 ? (
            <div className="mt-3">
              <p className="type-caption font-semibold text-jc-ink-secondary">
                <Msg
                  id="cvJobMatch.mentionedIntro"
                  defaultMessage="Your CV mentions these but doesn't list them as skills. Add them so recruiters and search see them:"
                  description="Intro above the skills the CV text mentions but the skills list doesn't include"
                />
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {result.mentioned.slice(0, MAX_CHIPS).map((s) => (
                  <li key={s}>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Plus}
                      onClick={() => addSkill(s)}
                      aria-label={formatMessage(
                        {
                          id: 'cvJobMatch.addSkillLabel',
                          defaultMessage: 'Add {skill} to your CV skills',
                          description: 'Accessible label of the button that adds a skill to the CV',
                        },
                        { skill: s },
                      )}
                    >
                      {s}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.missing.length > 0 ? (
            <div className="mt-3">
              <p className="type-caption font-semibold text-jc-ink-secondary">
                <Msg
                  id="cvJobMatch.missingIntro"
                  defaultMessage="Not on your CV:"
                  description="Intro above the job's skills the CV doesn't mention"
                />
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {result.missing.slice(0, MAX_CHIPS).map((s) => (
                  <li key={s}>
                    <Chip>{s}</Chip>
                  </li>
                ))}
              </ul>
              <p className="type-caption mt-1.5 text-jc-ink-secondary">
                <Msg
                  id="cvJobMatch.missingHonesty"
                  defaultMessage="If you have one of these, add it to your CV with where you used it. If not, the job may still be worth applying for: ads list wishes, not only must-haves."
                  description="Note under the missing skills on the job page CV match card"
                />
              </p>
            </div>
          ) : null}
        </>
      ) : (
        <h2 id={headingId} className="type-subheading mt-1 text-jc-ink">
          <Msg
            id="cvJobMatch.requirementsHeadline"
            defaultMessage="Requirements against your CV"
            description="Headline of the job page CV match card when the job lists no skills, only languages or experience"
          />
        </h2>
      )}

      {result.requiredLanguages.length > 0 || result.requiredYears ? (
        <ul className="mt-4 flex flex-col gap-1.5 border-t border-jc-border pt-3 text-sm">
          {result.requiredLanguages.map((l) => (
            <li key={l.name} className="flex items-center gap-2 text-jc-ink">
              {l.met ? (
                <Icon icon={Check} size="md" className="shrink-0 text-jc-forest" />
              ) : (
                <Icon icon={X} size="md" className="shrink-0 text-jc-ink" />
              )}
              {l.met ? (
                <Msg
                  id="cvJobMatch.languageMet"
                  defaultMessage="{language} required: on your CV"
                  description="Job page CV match: a required language the CV lists"
                  values={{ language: l.name }}
                />
              ) : (
                <Msg
                  id="cvJobMatch.languageMissing"
                  defaultMessage="{language} required: not on your CV"
                  description="Job page CV match: a required language the CV does not list"
                  values={{ language: l.name }}
                />
              )}
            </li>
          ))}
          {result.requiredYears ? (
            <li className="flex items-center gap-2 text-jc-ink">
              {result.cvYears !== null && result.cvYears >= result.requiredYears ? (
                <Icon icon={Check} size="md" className="shrink-0 text-jc-forest" />
              ) : (
                <span
                  className="inline-block size-5 shrink-0 text-center text-sm font-semibold text-jc-ink-secondary"
                  aria-hidden
                >
                  ?
                </span>
              )}
              {result.cvYears !== null ? (
                <Msg
                  id="cvJobMatch.years"
                  defaultMessage="Asks for {required}+ years of experience. Your CV shows about {years}."
                  description="Job page CV match: required years of experience vs years on the CV"
                  values={{ required: result.requiredYears, years: result.cvYears }}
                />
              ) : (
                <Msg
                  id="cvJobMatch.yearsUnknown"
                  defaultMessage="Asks for {required}+ years of experience. Add dates to your roles to compare."
                  description="Job page CV match: required years, when the CV has no readable dates"
                  values={{ required: result.requiredYears }}
                />
              )}
            </li>
          ) : null}
        </ul>
      ) : null}
    </>
  );
}
